import { useCallback, useEffect, useRef, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import UndoRoundedIcon from '@mui/icons-material/UndoRounded'
import QrCodeScannerRoundedIcon from '@mui/icons-material/QrCodeScannerRounded'
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded'
import BadgeScanner from '../components/BadgeScanner'
import {
  adminService,
  type AttendeeRow,
  type CheckinResult,
  type CheckinSummary,
} from '../../services/adminService'
import { LoadingState } from '../../components/ui/States'
import { apiError } from '../errors'
import { useToast } from '../useToast'

type ScanFeedback = {
  severity: 'success' | 'info' | 'error'
  message: string
}

export default function CheckinManager({ eventId }: { eventId: string }) {
  const { notify } = useToast()
  const [summary, setSummary] = useState<CheckinSummary | null>(null)
  const [attendees, setAttendees] = useState<AttendeeRow[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [scan, setScan] = useState('')
  const [cameraOpen, setCameraOpen] = useState(false)
  const [scanPaused, setScanPaused] = useState(false)
  const [feedback, setFeedback] = useState<ScanFeedback | null>(null)
  const resumeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const load = useCallback(
    () =>
      Promise.all([
        adminService.checkinSummary(eventId),
        adminService.listAttendees(eventId),
      ])
        .then(([s, a]) => {
          setSummary(s)
          setAttendees(a)
        })
        .catch((e) => notify(apiError(e), 'error'))
        .finally(() => setLoading(false)),
    [eventId, notify],
  )

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId])

  useEffect(() => () => {
    if (resumeTimer.current) clearTimeout(resumeTimer.current)
  }, [])

  /** Shared check-in-by-token path used by both manual entry and the camera. */
  const submitToken = useCallback(
    async (token: string): Promise<CheckinResult | null> => {
      const t = token.trim()
      if (!t) return null
      try {
        const res = await adminService.scanCheckin(eventId, t)
        notify(
          res.alreadyCheckedIn
            ? `${res.fullName || res.email} was already checked in`
            : `Checked in ${res.fullName || res.email}`,
          res.alreadyCheckedIn ? 'info' : 'success',
        )
        load()
        return res
      } catch (e) {
        notify(apiError(e), 'error')
        throw e
      }
    },
    [eventId, notify, load],
  )

  const doScan = async () => {
    try {
      await submitToken(scan)
      setScan('')
    } catch {
      /* toast already shown */
    }
  }

  /** Camera decode handler: check in, flash a result banner, briefly pause,
   *  then resume continuous scanning for the next person. */
  const handleCameraDecode = useCallback(
    async (token: string) => {
      setScanPaused(true)
      try {
        const res = await submitToken(token)
        if (res) {
          setFeedback(
            res.alreadyCheckedIn
              ? { severity: 'info', message: `${res.fullName || res.email} — already checked in` }
              : { severity: 'success', message: `Checked in ${res.fullName || res.email}` },
          )
        }
      } catch {
        setFeedback({ severity: 'error', message: 'Invalid or unrecognized code' })
      }
      if (resumeTimer.current) clearTimeout(resumeTimer.current)
      resumeTimer.current = setTimeout(() => {
        setScanPaused(false)
        setFeedback(null)
      }, 1600)
    },
    [submitToken],
  )

  const toggle = async (a: AttendeeRow, undo: boolean) => {
    try {
      await adminService.manualCheckin(eventId, a.id, undo)
      load()
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  if (loading) return <LoadingState />

  const pct = summary && summary.total ? Math.round((summary.checkedIn / summary.total) * 100) : 0
  const filtered = attendees.filter((a) => {
    const q = search.toLowerCase()
    return (
      !q ||
      a.email.toLowerCase().includes(q) ||
      (a.fullName || '').toLowerCase().includes(q)
    )
  })

  return (
    <Box>
      {summary && (
        <Card variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight={800}>
                {summary.checkedIn} / {summary.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                checked in ({pct}%)
              </Typography>
            </Box>
            <Box sx={{ flex: 2 }}>
              <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 4 }} />
            </Box>
          </Stack>
        </Card>
      )}

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Typography variant="subtitle1" fontWeight={700}>
          Scan a badge
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant={cameraOpen ? 'contained' : 'outlined'}
            startIcon={<QrCodeScannerRoundedIcon />}
            onClick={() => setCameraOpen((v) => !v)}
          >
            {cameraOpen ? 'Close camera' : 'Scan with camera'}
          </Button>
          <Button
            size="small"
            variant="text"
            startIcon={<OpenInFullRoundedIcon />}
            component={RouterLink}
            to={`/admin/events/${eventId}/checkin/scan`}
          >
            Kiosk mode
          </Button>
        </Stack>
      </Stack>

      {cameraOpen && (
        <Box sx={{ mb: 2 }}>
          <BadgeScanner onDecode={handleCameraDecode} paused={scanPaused} />
          {feedback && (
            <Alert severity={feedback.severity} sx={{ mt: 1 }}>
              {feedback.message}
            </Alert>
          )}
        </Box>
      )}

      <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Paste or scan the badge code…"
          value={scan}
          onChange={(e) => setScan(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') doScan()
          }}
        />
        <Button variant="contained" onClick={doScan} disabled={!scan.trim()}>
          Check in
        </Button>
      </Stack>

      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
        Attendees
      </Typography>
      <TextField
        size="small"
        fullWidth
        placeholder="Search by name or email"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2 }}
      />
      <Stack spacing={1}>
        {filtered.slice(0, 100).map((a) => {
          const isIn = Boolean(a.checkedInAt)
          return (
            <Card key={a.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight={700} noWrap>
                  {a.fullName || a.email}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {a.email}
                </Typography>
              </Box>
              {isIn ? (
                <>
                  <Chip
                    size="small"
                    color="success"
                    icon={<CheckCircleRoundedIcon sx={{ fontSize: 15 }} />}
                    label="In"
                    sx={{ fontWeight: 700 }}
                  />
                  <IconButton size="small" title="Undo" onClick={() => toggle(a, true)}>
                    <UndoRoundedIcon fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <Button size="small" variant="outlined" onClick={() => toggle(a, false)}>
                  Check in
                </Button>
              )}
            </Card>
          )
        })}
      </Stack>
    </Box>
  )
}
